import { i18n } from '@lingui/core';
import { Trans, t } from '@lingui/macro';
import IconChevronDown from '@we-make-websites/ui-lib/icons/direction/chevron/down.svg';
import IconTick from '@we-make-websites/ui-lib/icons/misc/tick.svg';
import clsx from 'clsx';
import type { SortByItem } from 'instantsearch.js/es/connectors/sort-by/connectSortBy';
import { useRouter } from 'next/router';
import type { SortByProps } from 'react-instantsearch';
import { useSortBy } from 'react-instantsearch';
import { useMediaMatch } from 'rooks';
import { useContext } from 'use-context-selector';
import { storeLocale } from '@/root/constants';
import {
  algoliaProductIndex,
  algoliaEduCourseIndex,
} from 'utils/algoliaConstants';
import {
  DropdownButton,
  DropdownList,
  DropdownMenu,
} from '../dropdownMenu/DropdownMenu';
import { useFeatureFlags } from '../featureFlagsProvider/FeatureFlagsContext';
import Overlay from '../overlay/Overlay';
import { OverlayContext } from '../overlay/OverlayContext';
import filterStyles from './Filters.module.scss';
import styles from './Sorting.module.scss';

export const Sorting = ({
  mobile = false,
  isEduCourse = false,
}: {
  mobile?: boolean;
  isEduCourse?: boolean;
}) => {
  const { locale: routerLocale } = useRouter();
  const locale = storeLocale(routerLocale);

  const { featureFlags } = useFeatureFlags();
  const { showPrice } = featureFlags[locale];

  let sortingOptions = [
    {
      label: i18n._(
        /* i18n */ {
          id: 'filters.sorting.featured',
          message: 'Featured',
        }
      ),
      value: algoliaProductIndex(locale),
    },
    // Only show price sorting options if the feature flag is enabled
    ...(showPrice
      ? [
          {
            label: i18n._(
              /* i18n */ {
                id: 'filters.sorting.priceAscending',
                message: 'Price: Ascending',
              }
            ),
            value: `${algoliaProductIndex(locale)}_price_asc`,
          },
          {
            label: i18n._(
              /* i18n */ {
                id: 'filters.sorting.priceDescending',
                message: 'Price: Descending',
              }
            ),
            value: `${algoliaProductIndex(locale)}_price_desc`,
          },
        ]
      : []),
    {
      label: i18n._(
        /* i18n */ {
          id: 'filters.sorting.publishedAtDescending',
          message: 'New',
        }
      ),
      value: `${algoliaProductIndex(locale)}_published_at_desc`,
    },
    {
      label: i18n._(
        /* i18n */ {
          id: 'filters.sorting.publishedAtAscending',
          message: 'Oldest first',
        }
      ),
      value: `${algoliaProductIndex(locale)}_published_at_asc`,
    },
  ];

  if (isEduCourse) {
    sortingOptions = [
      {
        label: i18n._(
          /* i18n */ {
            id: 'filters.sorting.featured',
            message: 'Featured',
          }
        ),
        value: algoliaEduCourseIndex(locale),
      },
      {
        label: i18n._(
          /* i18n */ {
            id: 'filters.sorting.publishedAtDescending',
            message: 'New',
          }
        ),
        value: `${algoliaEduCourseIndex(locale)}_updated_at_desc`,
      },
      {
        label: i18n._(
          /* i18n */ {
            id: 'filters.sorting.publishedAtAscending',
            message: 'Oldest first',
          }
        ),
        value: `${algoliaEduCourseIndex(locale)}_updated_at_asc`,
      },
      {
        label: i18n._(
          /* i18n */ {
            id: 'filters.sorting.filters.sorting.a_to_z',
            message: 'A to Z',
          }
        ),
        value: `${algoliaEduCourseIndex(locale)}_a_z`,
      },
      {
        label: i18n._(
          /* i18n */ {
            id: 'filters.sorting.filters.sorting.z_to_a',
            message: 'Z to A',
          }
        ),
        value: `${algoliaEduCourseIndex(locale)}_z_a`,
      },
    ];
  }

  if (mobile) {
    return <MobileSorting items={sortingOptions} />;
  }

  return <DesktopSorting items={sortingOptions} />;
};

const DesktopSorting = (props: SortByProps) => {
  const { currentRefinement, options, refine } = useSortBy(props);

  const handleOptionClick = (value: SortByItem['value']) => {
    refine(value);
  };

  const currentRefinementObject = options.find((option) => {
    return option.value === currentRefinement;
  });

  const currentRefinementLabel = currentRefinementObject?.label ?? '';

  return (
    <DropdownMenu>
      <DropdownButton
        className={clsx(
          filterStyles.filters__menuButton,
          filterStyles.filters__sorting
        )}
        aria-label={t({
          id: 'sorting.dropdown.label',
          message: `Sort by ${currentRefinementLabel}, opens drop down`,
        })}
      >
        <span className="text-utility-utility">
          <Trans id="filters.sorting">Sort By</Trans>
          {currentRefinementLabel && (
            <>
              <span>:</span>
              <span className={styles.sorting__dropdownCurrentSorting}>
                {currentRefinementLabel}
              </span>
            </>
          )}
        </span>
        <IconChevronDown />
      </DropdownButton>

      <DropdownList
        className={filterStyles.filters__menuList}
        key="Sorting-desktop"
        directionX="right"
      >
        <CustomSortBy
          handleOptionClick={handleOptionClick}
          options={options}
          currentRefinement={currentRefinement}
        />
      </DropdownList>
    </DropdownMenu>
  );
};

const MobileSorting = (props: SortByProps) => {
  const { open, close } = useContext(OverlayContext);
  const namespace = 'mobile-sorting';
  const { currentRefinement, options, refine } = useSortBy(props);

  /**
   * Custom handle closing the overlay on route change.
   */
  const customHandleRouteChange = (
    url: string,
    { shallow }: { shallow?: boolean }
  ) => {
    /* Don't close on shallow route change. */
    if (shallow) {
      return;
    }
    close(namespace);
  };

  const isDesktop = useMediaMatch('(min-width: 1024px)');

  if (isDesktop) {
    close(namespace);
  }

  const handleOptionClick = (value: SortByItem['value']) => {
    refine(value);
  };

  return (
    <div>
      <button
        className={clsx(
          filterStyles.filters__menuButton,
          filterStyles.filters__sorting
        )}
        type="button"
        onClick={() => open({ namespace })}
      >
        <span className="text-utility-utility">
          <Trans id="filters.sorting">Sort By</Trans>
        </span>
        <IconChevronDown />
      </button>

      <Overlay
        type="mobile-drawer"
        blockClass="mobileFilters"
        namespace={namespace}
        direction="up"
        useReachUi={false}
        customHandleRouteChange={customHandleRouteChange}
      >
        <p
          className={clsx(
            styles.sorting__mobileTitle,
            'text-utility-utility-mobile'
          )}
        >
          <Trans id="filters.sorting.action">Sorting</Trans>
        </p>
        <div className={styles.sorting__mobileListWrapper}>
          <CustomSortBy
            handleOptionClick={handleOptionClick}
            options={options}
            currentRefinement={currentRefinement}
          />
        </div>
      </Overlay>
    </div>
  );
};

const CustomSortBy = ({
  handleOptionClick,
  options,
  currentRefinement,
}: {
  options: SortByItem[];
  handleOptionClick: (value: SortByItem['value']) => void;
  currentRefinement: string;
}) => {
  return (
    <ul>
      {options.map((option) => (
        <li
          key={option.value}
          className={clsx(filterStyles.filters__item, styles.sorting__item)}
        >
          <button
            className={filterStyles.filters__itemButton}
            type="button"
            data-active={currentRefinement === option.value}
            onClick={() => handleOptionClick(option.value)}
          >
            <IconTick />
            <span className="text-body-2-mobile text-body-2-desktop">
              {option.label}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
};
